/**
 * Use Debounce Hook
 *
 * Custom hook for debouncing a value with optional minimum length.
 *
 * @since 1.0.0
 */

import { useEffect, useState } from 'react';

import type { UseDebounceParams, UseDebounceReturn } from './types';

/**
 * Custom hook for debouncing a value
 *
 * @since 1.0.0
 *
 * @param params Hook parameters.
 *
 * @return Hook return value.
 */
export const useDebounce = ( params: UseDebounceParams ): UseDebounceReturn => {
	const { value, delay = 500, minLength = 0 } = params;
	const [ debouncedValue, setDebouncedValue ] = useState< string >( value );

	useEffect( () => {
		// If the value is shorter than minLength, clear the debounced value
		if ( minLength > 0 && value.length < minLength ) {
			setDebouncedValue( '' );
			return;
		}

		const handler = setTimeout( () => {
			setDebouncedValue( value );
		}, delay );

		return () => {
			clearTimeout( handler );
		};
	}, [ value, delay, minLength ] );

	return {
		debouncedValue,
	};
};

